import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Mail, Calendar as CalendarIcon, Check, Copy, ExternalLink, Plug, RefreshCw, X, Info } from "lucide-react";

export default function Integrations() {
  const { activeWorkspaceId, user, memberships } = useAuth();
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState({});
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // provider dict when opening dialog
  const isWsAdmin = useMemo(() => {
    if (user?.is_super_admin) return true;
    const m = (memberships || []).find((x) => x.workspace_id === activeWorkspaceId);
    return ["owner", "admin", "super_admin"].includes(m?.role || "");
  }, [user, memberships, activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/oauth-integrations`);
      setProviders(data.providers || []);
      // For each provider, fetch my connection status
      const connMap = {};
      for (const p of (data.providers || []).map((x) => x.provider)) {
        try {
          const { data: st } = await api.get(`/workspaces/${activeWorkspaceId}/oauth/${p}/status`);
          connMap[p] = st;
        } catch { connMap[p] = { connected: false }; }
      }
      setConnections(connMap);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load integrations");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, isWsAdmin]);

  // Handle popup postMessage from OAuth callback
  useEffect(() => {
    const handler = (evt) => {
      if (!evt.data || evt.data.type !== "acquilix_oauth") return;
      if (evt.data.status === "ok") {
        toast.success("Connected successfully");
        load();
      } else {
        toast.error(evt.data.message || "Connection failed");
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line
  }, [activeWorkspaceId]);

  const startConnect = async (providerId) => {
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/oauth/${providerId}/start`);
      window.open(data.url, "acquilix-oauth", "width=520,height=680,menubar=0,toolbar=0");
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Could not start OAuth. Is the workspace admin configured?");
    }
  };

  const disconnect = async (providerId) => {
    if (!window.confirm("Disconnect this account?")) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/oauth/${providerId}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Disconnect failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <PageHeader
        eyebrow="Workspace"
        title="Integrations"
        subtitle="Connect Google, Microsoft, and other services. Workspace owners configure the app; each member connects their own account."
      />

      <div className="border border-border bg-muted/20 p-4 flex items-start gap-3" data-testid="integrations-info">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-muted-foreground">
          OAuth credentials are stored <strong>per workspace</strong>. That means every workspace uses its own
          Google Cloud project — safer, and you (the owner) keep full control over quota, consent screen and revocation.
          Connections themselves are <strong>per user</strong> — your Gmail signature is only used when Acquilix
          sends email <em>on your behalf</em>.
        </p>
      </div>

      {providers.map((p) => (
        <ProviderCard
          key={p.provider}
          p={p}
          isWsAdmin={isWsAdmin}
          myConnection={connections[p.provider]}
          onEdit={() => setEditing(p)}
          onConnect={() => startConnect(p.provider)}
          onDisconnect={() => disconnect(p.provider)}
          onRefetchSignature={async () => {
            try {
              const { data } = await api.post(`/workspaces/${activeWorkspaceId}/oauth/${p.provider}/signature/refetch`);
              toast.success(data.signature_html ? "Signature re-fetched from Gmail" : "No default signature found in Gmail");
              load();
            } catch (e) { toast.error(apiErrorMessage(e) || "Refetch failed"); }
          }}
          onSignatureOverride={async (html) => {
            try {
              await api.patch(`/workspaces/${activeWorkspaceId}/oauth/${p.provider}/signature`, { signature_html: html });
              toast.success("Signature updated");
              load();
            } catch (e) { toast.error(apiErrorMessage(e) || "Update failed"); }
          }}
        />
      ))}

      {isWsAdmin && editing && (
        <ProviderConfigDialog
          wsId={activeWorkspaceId}
          provider={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <SharedMailboxSection wsId={activeWorkspaceId} isWsAdmin={isWsAdmin} />

      {busy && <div className="text-sm text-muted-foreground">Loading…</div>}
    </div>
  );
}

// ──────────────── Provider card ────────────────

function ProviderCard({ p, isWsAdmin, myConnection, onEdit, onConnect, onDisconnect, onRefetchSignature, onSignatureOverride }) {
  const configured = p.configured && p.enabled;
  const conn = myConnection || {};
  const features = p.features || {};

  return (
    <div className="border border-border p-5 space-y-4" data-testid={`provider-${p.provider}`}>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 border border-border bg-white flex items-center justify-center text-lg font-bold" style={{ fontFamily: "'Product Sans', Arial" }}>
          <span style={{ background: "linear-gradient(90deg,#4285F4,#EA4335,#FBBC05,#34A853)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>G</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">{p.name}</h3>
            {configured ? (
              <span className="text-[10px] tracking-widest uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">Ready</span>
            ) : (
              <span className="text-[10px] tracking-widest uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">Not configured</span>
            )}
            {features.gmail_send && <FeaturePill icon={Mail} label="Gmail send" />}
            {features.calendar_sync && <FeaturePill icon={CalendarIcon} label="Calendar sync" />}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Available features:{" "}
            {Object.entries(p.available_features || {}).map(([k, v]) => v.name).join(" · ") || "—"}
          </p>
        </div>
        {isWsAdmin && (
          <Button variant="outline" className="rounded-none" onClick={onEdit} data-testid={`btn-configure-${p.provider}`}>
            {configured ? "Edit config" : "Configure"}
          </Button>
        )}
      </div>

      {configured && (
        <div className="border-t border-border pt-4">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">My connected account</div>
          {conn.connected ? (
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-1 min-w-[250px]">
                {conn.account_picture && <img src={conn.account_picture} alt="" className="w-10 h-10 rounded-full border border-border" />}
                <div>
                  <div className="font-semibold text-sm">{conn.account_name || conn.account_email}</div>
                  <div className="text-xs text-muted-foreground">{conn.account_email}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-none h-8" onClick={onConnect} data-testid={`btn-reconnect-${p.provider}`}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reconnect
                </Button>
                <Button variant="ghost" className="rounded-none h-8 text-red-600 hover:text-red-700" onClick={onDisconnect} data-testid={`btn-disconnect-${p.provider}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <Button className="rounded-none" onClick={onConnect} data-testid={`btn-connect-${p.provider}`}>
              <Plug className="w-4 h-4 mr-2" /> Connect {p.name.split("(")[0].trim()}
            </Button>
          )}

          {conn.connected && features.gmail_send && (
            <SignatureBlock conn={conn} onRefetch={onRefetchSignature} onOverride={onSignatureOverride} />
          )}
          {conn.connected && features.calendar_sync && (
            <CalendarSyncBlock />
          )}
        </div>
      )}

      {!configured && !isWsAdmin && (
        <p className="text-sm text-muted-foreground">
          Your workspace owner hasn&apos;t configured this integration yet.
        </p>
      )}
    </div>
  );
}

function FeaturePill({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-border bg-background px-2 py-0.5">
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

// ──────────────── Signature editor ────────────────

function SignatureBlock({ conn, onRefetch, onOverride }) {
  const [showEdit, setShowEdit] = useState(false);
  const [draft, setDraft] = useState(conn.signature_html || "");
  useEffect(() => setDraft(conn.signature_html || ""), [conn.signature_html]);
  return (
    <div className="mt-4 border border-dashed border-border p-3 space-y-2" data-testid="signature-block">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Signature</span>
        {conn.signature_source && (
          <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5">
            {conn.signature_source === "auto" ? "Auto-fetched from Gmail" : "Manual override"}
          </span>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="ghost" size="sm" className="rounded-none h-7" onClick={onRefetch} data-testid="btn-refetch-signature">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Re-fetch from Gmail
          </Button>
          <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => setShowEdit(true)} data-testid="btn-edit-signature">
            Edit
          </Button>
        </div>
      </div>
      {conn.signature_html ? (
        <div className="border border-border bg-white p-2 text-sm max-h-28 overflow-y-auto" dangerouslySetInnerHTML={{ __html: conn.signature_html }} />
      ) : (
        <p className="text-xs text-muted-foreground">No signature set. Re-fetch from Gmail or add a manual override.</p>
      )}
      {showEdit && (
        <Dialog open onOpenChange={(v) => !v && setShowEdit(false)}>
          <DialogContent className="max-w-2xl" data-testid="signature-dialog">
            <DialogHeader><DialogTitle>Edit signature (HTML)</DialogTitle></DialogHeader>
            <Textarea rows={10} value={draft} onChange={(e) => setDraft(e.target.value)} className="font-mono text-xs" data-testid="signature-textarea" />
            <div className="border border-border bg-muted/20 p-2 text-sm max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: draft }} />
            <DialogFooter>
              <Button variant="ghost" className="rounded-none" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button className="rounded-none" onClick={() => { onOverride(draft); setShowEdit(false); }} data-testid="btn-save-signature">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ──────────────── Admin config dialog ────────────────

function ProviderConfigDialog({ wsId, provider, onClose, onSaved }) {
  const [clientId, setClientId] = useState(provider.client_id || "");
  const [clientSecret, setClientSecret] = useState("");
  const [enabled, setEnabled] = useState(provider.enabled ?? true);
  const [gmailOn, setGmailOn] = useState(!!provider.features?.gmail_send);
  const [calendarOn, setCalendarOn] = useState(!!provider.features?.calendar_sync);
  const [busy, setBusy] = useState(false);
  const setup = provider.setup || {};

  const copyRedirect = () => {
    navigator.clipboard.writeText(provider.redirect_uri || "");
    toast.success("Redirect URI copied");
  };

  const save = async () => {
    if (!clientId.trim()) { toast.error("Client ID is required"); return; }
    if (!provider.configured && !clientSecret.trim()) { toast.error("Client Secret is required on first save"); return; }
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/oauth-integrations/${provider.provider}`, {
        client_id: clientId.trim(),
        client_secret: clientSecret.trim() || undefined,
        enabled,
        features: { gmail_send: gmailOn, calendar_sync: calendarOn },
      });
      toast.success("Integration saved");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this integration? All connected users will be disconnected.")) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${wsId}/oauth-integrations/${provider.provider}`);
      toast.success("Integration removed");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="provider-config-dialog">
        <DialogHeader><DialogTitle>Configure — {provider.name}</DialogTitle></DialogHeader>

        <div className="space-y-4">
          <div className="border border-border bg-muted/20 p-3 space-y-2 text-sm">
            <div className="font-semibold flex items-center gap-2"><Info className="w-4 h-4" /> Setup steps</div>
            <ol className="list-decimal ml-5 space-y-1 text-muted-foreground">
              <li>Open the <a href={setup.console_url} target="_blank" rel="noreferrer" className="underline">Google Cloud Console <ExternalLink className="inline w-3 h-3" /></a> and select (or create) a project.</li>
              <li>Enable these APIs: <strong>{(setup.enable_apis || []).join(", ")}</strong>.</li>
              <li>OAuth consent screen → External → add your app info and test users.</li>
              <li>Create Credentials → OAuth client ID → <strong>Web application</strong>.</li>
              <li>Add this <strong>Authorized redirect URI</strong> exactly:</li>
            </ol>
            <div className="flex items-center gap-2 mt-1">
              <code className="text-xs bg-black text-emerald-300 px-2 py-1 flex-1 overflow-x-auto" data-testid="redirect-uri">{provider.redirect_uri}</code>
              <Button variant="outline" size="sm" className="rounded-none h-7" onClick={copyRedirect} data-testid="btn-copy-redirect">
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">{setup.redirect_note}</p>
          </div>

          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Client ID *</Label>
            <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="123456789-abc.apps.googleusercontent.com" className="h-10 font-mono text-xs" data-testid="input-client-id" />
          </div>

          <div>
            <Label className="block mb-1.5 text-sm font-semibold">
              Client Secret {provider.configured ? <span className="text-xs text-muted-foreground font-normal">(leave blank to keep existing)</span> : "*"}
            </Label>
            <Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={provider.client_secret_mask || "GOCSPX-…"} className="h-10 font-mono text-xs" data-testid="input-client-secret" />
          </div>

          <div className="border border-border p-3 space-y-2">
            <Label className="block text-sm font-semibold mb-1">Enable features</Label>
            <label className="flex items-center gap-3 py-1">
              <Switch checked={gmailOn} onCheckedChange={setGmailOn} data-testid="toggle-gmail" />
              <div>
                <div className="text-sm font-medium">Gmail Send-As</div>
                <div className="text-xs text-muted-foreground">Send Acquilix emails from each user&apos;s Gmail with their signature.</div>
              </div>
            </label>
            <label className="flex items-center gap-3 py-1">
              <Switch checked={calendarOn} onCheckedChange={setCalendarOn} data-testid="toggle-calendar" />
              <div>
                <div className="text-sm font-medium">Google Calendar two-way sync</div>
                <div className="text-xs text-muted-foreground">Push Acquilix calendar events to Google Calendar, pull remote changes back. Connected users can Sync now / Push / Pull from the panel below.</div>
              </div>
            </label>
          </div>

          <label className="flex items-center gap-3">
            <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="toggle-enabled" />
            <span className="text-sm">Integration enabled for this workspace</span>
          </label>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {provider.configured && (
            <Button variant="destructive" className="rounded-none mr-auto" onClick={remove} disabled={busy} data-testid="btn-remove-provider">
              Remove integration
            </Button>
          )}
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="btn-save-provider">
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ──────────────── Google Calendar sync block ────────────────

function CalendarSyncBlock() {
  const { activeWorkspaceId } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/google-calendar/status`);
      setStatus(data);
    } catch { setStatus({ connected: false, calendar_sync_enabled: false }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/google-calendar/sync-now`);
      toast.success(`Synced — pushed ${data.push.pushed} · pulled ${data.pull.inserted}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Sync failed"); }
    finally { setBusy(false); }
  };
  const pushOnly = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/google-calendar/push`);
      toast.success(`Pushed ${data.pushed} · updated ${data.updated}${data.failed ? ` · ${data.failed} failed` : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Push failed"); }
    finally { setBusy(false); }
  };
  const pullOnly = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/google-calendar/pull`, { force_full: !!force });
      toast.success(`Pulled ${data.inserted} new · ${data.updated} updated · ${data.deleted} removed`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Pull failed"); }
    finally { setBusy(false); }
  };

  if (!status?.calendar_sync_enabled) {
    return (
      <div className="mt-4 border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="calendar-sync-disabled">
        Calendar sync scope isn&apos;t granted on this connection. Reconnect after enabling Calendar sync in the workspace admin config to activate two-way sync.
      </div>
    );
  }
  return (
    <div className="mt-4 border border-dashed border-border p-3 space-y-2" data-testid="calendar-sync-block">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Two-way Calendar Sync</span>
        {status?.last_pull_at ? (
          <span className="text-[11px] text-muted-foreground">
            Last sync: {new Date(status.last_pull_at).toLocaleString()}
          </span>
        ) : (
          <span className="text-[11px] text-amber-700">Never synced</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Pushes events you create in Acquilix to your primary Google Calendar, and pulls remote changes into a personal &quot;Google (external)&quot; calendar.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="rounded-none h-8" onClick={syncNow} disabled={busy} data-testid="btn-gcal-sync-now">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={pushOnly} disabled={busy} data-testid="btn-gcal-push">
          Push only
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => pullOnly(false)} disabled={busy} data-testid="btn-gcal-pull">
          Pull only
        </Button>
        <Button size="sm" variant="ghost" className="rounded-none h-8" onClick={() => pullOnly(true)} disabled={busy} data-testid="btn-gcal-pull-full">
          Force full re-pull
        </Button>
      </div>
    </div>
  );
}

// ──────────────── Shared workspace mailbox section ────────────────

function SharedMailboxSection({ wsId, isWsAdmin }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ address: "", display_name: "", reply_to: "", signature_html: "", inbound_webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const [showSecretField, setShowSecretField] = useState(false);

  const load = async () => {
    if (!wsId) return;
    try {
      const { data } = await api.get(`/workspaces/${wsId}/shared-mailbox`);
      setCfg(data);
      if (data.configured) {
        setForm({
          address: data.address || "",
          display_name: data.display_name || "",
          reply_to: data.reply_to || "",
          signature_html: data.signature_html || "",
          inbound_webhook_secret: "",
        });
      }
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.inbound_webhook_secret) delete payload.inbound_webhook_secret;
      if (!payload.reply_to) delete payload.reply_to;
      if (!payload.display_name) delete payload.display_name;
      await api.put(`/workspaces/${wsId}/shared-mailbox`, payload);
      toast.success("Shared mailbox saved");
      setShowSecretField(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/shared-mailbox/verify`);
      toast.success(`Verified via ${data.method}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Verify failed"); }
    finally { setBusy(false); }
  };
  const copy = async (v) => {
    try {
      await navigator.clipboard.writeText(v);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — your browser may block clipboard on insecure origins");
    }
  };

  return (
    <div className="border border-border p-5 space-y-4" data-testid="shared-mailbox-section">
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 border border-border bg-white flex items-center justify-center">
          <Mail className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">Shared workspace mailbox</h3>
            {cfg?.configured && cfg?.verified_at ? (
              <span className="text-[10px] tracking-widest uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">Verified</span>
            ) : cfg?.configured ? (
              <span className="text-[10px] tracking-widest uppercase bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">Awaiting DNS</span>
            ) : (
              <span className="text-[10px] tracking-widest uppercase bg-muted text-foreground/70 border border-border px-2 py-0.5">Not configured</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Powered by Resend Inbound. Any workspace member can reply as this address — nothing depends on individual Gmail accounts. Survives every off-boarding.
          </p>
        </div>
      </div>

      {!isWsAdmin ? (
        cfg?.configured ? (
          <p className="text-sm text-muted-foreground">
            Shared address: <strong>{cfg.address}</strong>{cfg.verified_at ? " (verified)" : " (pending verification)"}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Your workspace owner hasn&apos;t configured this yet.</p>
        )
      ) : (
        <div className="space-y-4 border-t border-border pt-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Shared address *</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                     placeholder="hello@yourdomain.com" className="h-10 rounded-none font-mono" data-testid="smb-address" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Display name</Label>
              <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                     placeholder="Acme Studio" className="h-10 rounded-none" data-testid="smb-display-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Reply-to (optional)</Label>
              <Input value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })}
                     placeholder="ops@yourdomain.com" className="h-10 rounded-none font-mono" data-testid="smb-replyto" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Webhook secret (optional but recommended)</Label>
              <div className="flex gap-2">
                <Input type={showSecretField ? "text" : "password"}
                       value={form.inbound_webhook_secret}
                       onChange={(e) => setForm({ ...form, inbound_webhook_secret: e.target.value })}
                       placeholder={cfg?.webhook_secret_set ? "•••••• (leave blank to keep)" : "Paste svix secret"}
                       className="h-10 rounded-none font-mono flex-1" data-testid="smb-secret" />
                <Button variant="ghost" size="sm" className="rounded-none h-10" type="button" onClick={() => setShowSecretField((s) => !s)}>
                  {showSecretField ? "Hide" : "Show"}
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Shared signature (HTML)</Label>
            <Textarea rows={4} value={form.signature_html} onChange={(e) => setForm({ ...form, signature_html: e.target.value })}
                      placeholder="<p>— The Acme Studio Team</p>" className="font-mono text-xs rounded-none" data-testid="smb-signature" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button className="rounded-none" onClick={save} disabled={busy || !form.address} data-testid="smb-save">
              {busy ? "Saving…" : "Save mailbox config"}
            </Button>
            {cfg?.configured && !cfg?.verified_at && (
              <Button variant="outline" className="rounded-none" onClick={verify} disabled={busy} data-testid="smb-verify">
                <Check className="w-4 h-4 mr-1" /> Verify domain
              </Button>
            )}
          </div>

          {cfg?.configured && cfg?.dns && (
            <div className="border-t border-border pt-4 space-y-2" data-testid="smb-dns">
              <div className="label-eyebrow">DNS setup</div>
              <p className="text-xs text-muted-foreground">{cfg.dns.help}</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-2 border border-border">Type</th>
                      <th className="text-left p-2 border border-border">Host / Name</th>
                      <th className="text-left p-2 border border-border">Value</th>
                      <th className="text-left p-2 border border-border">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(cfg.dns.records || []).map((r, i) => (
                      <tr key={i} data-testid={`smb-dns-row-${r.type}`}>
                        <td className="p-2 border border-border font-mono">{r.type}{r.priority ? ` · pri ${r.priority}` : ""}</td>
                        <td className="p-2 border border-border font-mono break-all">
                          <button onClick={() => copy(r.host)} className="hover:underline inline-flex items-center gap-1">
                            <Copy className="w-3 h-3" /> {r.host}
                          </button>
                        </td>
                        <td className="p-2 border border-border font-mono break-all">
                          <button onClick={() => copy(r.value)} className="hover:underline inline-flex items-center gap-1">
                            <Copy className="w-3 h-3" /> {r.value}
                          </button>
                        </td>
                        <td className="p-2 border border-border text-muted-foreground">{r.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-muted-foreground">
                Inbound webhook URL (paste this in Resend&apos;s Inbound settings):
                <code className="ml-2 px-2 py-1 bg-muted font-mono text-xs">{cfg.inbound_webhook_url}</code>
                <button onClick={() => copy(cfg.inbound_webhook_url)} className="ml-1 inline-flex items-center gap-1 text-primary hover:underline">
                  <Copy className="w-3 h-3" /> copy
                </button>
              </div>
              <a href={cfg.dns.setup_url} target="_blank" rel="noreferrer"
                 className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
                Open Resend dashboard <ExternalLink className="w-3 h-3" />
              </a>
              {cfg?.last_inbound_at && (
                <div className="text-xs text-emerald-700">
                  ✓ Last inbound received: {new Date(cfg.last_inbound_at).toLocaleString()} — total received: {cfg.inbound_count}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
